/* eslint-disable complexity */
// TODO: remove eslint-disable by HCP-79008
import {useIsMaterialUnmeasured} from '@domain/material-storage'
import {Material, MaterialSource} from '@domain/materials'
import {Card, CardProps} from '@hconnect/uikit/src/lib2'
import {useUpmPlantContext} from '@hconnect/upm'
import {FeatureFlagKey, useCommonFeatureFlag} from '@hproduce/feature-flags'
import {Tooltip} from '@mui/material'
import React from 'react'
import {useTranslation} from 'react-i18next'
import {useNavigate} from 'react-router'

import {StockCardHeader} from '../components'
import {STOCK_CARD_WIDGET_NAMESPACE} from '../consts'
import {useEntersMaterialPlanEvent} from '../hooks'
import {RoutingState} from '../routing'

interface StockCard extends CardProps {
  title: React.ReactNode
  additionalHeader?: React.ReactNode
  material?: Material
  page: 'storage' | 'material'
  colSpan?: number
  children: React.ReactNode
  stockMaterialsOrdersUrl: string
  /**
   * Whether the user can view material orders. Gates navigation to the material detail page.
   * Defaults to `true` (lean roles). Host apps that enforce permissions should pass the
   * resolved value.
   */
  canViewMaterialOrders?: boolean
  /**
   * Optional click handler to override default navigation behavior.
   * When provided, the card will always be clickable.
   */
  onCardClick?: () => void
}

export const StockCard: React.FC<StockCard> = ({
  title,
  additionalHeader,
  colSpan,
  page,
  material,
  children,
  stockMaterialsOrdersUrl,
  canViewMaterialOrders = true,
  onCardClick,
  ...rest
}) => {
  const {t} = useTranslation(STOCK_CARD_WIDGET_NAMESPACE)
  const {plantId: plantCode} = useUpmPlantContext()
  const navigate = useNavigate()

  const trackPlannerEvent = useEntersMaterialPlanEvent()

  const {plantId} = useUpmPlantContext()
  const isRawMaterialsPlanningFeatureAvailable =
    useCommonFeatureFlag(FeatureFlagKey.RawMaterialsPlanning, {plantId}) ?? false

  const {data: isMaterialUnmeasured} = useIsMaterialUnmeasured(material?.id, plantCode)

  const canGoToBoughtToVendorPage: boolean =
    !!material &&
    material.source === MaterialSource.BoughtFromVendor &&
    canViewMaterialOrders &&
    isRawMaterialsPlanningFeatureAvailable

  const canGoToOverrideMaterials: boolean =
    !!material && (isMaterialUnmeasured ?? false) && isRawMaterialsPlanningFeatureAvailable

  const canGoToMaterialDetail: boolean =
    canGoToOverrideMaterials ||
    (canGoToBoughtToVendorPage && material?.products.length ? true : false)

  const boughtFromVendorIsDisabled =
    !canGoToOverrideMaterials && canGoToBoughtToVendorPage && !material?.products.length

  // Use provided onCardClick, or fall back to default navigation behavior
  const handleCardClick = onCardClick
    ? () => {
        trackPlannerEvent()
        onCardClick()
      }
    : canGoToMaterialDetail && !boughtFromVendorIsDisabled
      ? () => {
          trackPlannerEvent()
          void navigate(`${stockMaterialsOrdersUrl}/${material?.id}`, {
            state: {
              materialsVisitedFrom: page
            } as RoutingState
          })
        }
      : undefined

  const isClickable = !!onCardClick || canGoToMaterialDetail

  const card = (
    <Card
      {...rest}
      onClick={handleCardClick}
      sx={{
        cursor: isClickable
          ? boughtFromVendorIsDisabled && !onCardClick
            ? 'help'
            : 'pointer'
          : 'default',
        gridColumn: `span ${colSpan}`
      }}
      headerContent={
        <StockCardHeader
          title={title}
          material={material}
          canGoToBoughtToVendorPage={canGoToBoughtToVendorPage}
          additionalHeader={additionalHeader}
        />
      }
    >
      {children}
    </Card>
  )

  // Only show disabled tooltip when using default behavior (no onCardClick override)
  return !onCardClick && canGoToMaterialDetail && boughtFromVendorIsDisabled ? (
    <Tooltip title={t('boughtFromVendorDisabledTooltip')} followCursor={true} placement="bottom">
      <span>{card}</span>
    </Tooltip>
  ) : (
    card
  )
}
